import LegalPageLayout from './LegalPageLayout'

const content = `# Privacy Policy — EasyAppIcon

**Effective date:** 21 Jan 2026

**Who we are:** EasyIO trading as "EasyAppIcon" ("**EasyAppIcon**", "**we**", "**us**", "**our**").
**Contact (Privacy):** Attn: **Privacy Officer** — **[supports@easyappicon.com](mailto:supports@easyappicon.com)**
**Postal address:** *West Wing 2/F, 822 Lai Chi Kok Road, Cheung Sha Wan, Kowloon, Hong Kong*

This Privacy Policy explains how we collect, use, disclose, and protect personal data for the EasyAppIcon website, dashboard, and icon-generation API (the "**Services**"). If you do not agree, please do not use the Services.

---

## 1) Scope & Roles

**Services covered:** easyappicon.com website, account dashboard, and icon-generation API.

**Controller vs Processor:**

- **Controller:** We act as a **Controller** for account, billing, marketing preferences, and support data.
- **Processor:** When you use the API to upload images/files and generate outputs, we generally act as a **Processor** for **Customer Content** (your uploaded images/files and related parameters), processing it on your instructions and in accordance with our **Terms of Service** and **[Data Processing Addendum (DPA)](/dpa)** (see §10).

## 2) Data We Collect

**2.1 Account & Authentication**

- Email address
- Login credentials (password is stored only in **hashed** form by our authentication provider)
- Display name (optional)
- Plan tier and subscription status
- Consent timestamps (e.g., acceptance of Terms / Privacy Policy)

**2.2 Billing & Payments**

- Name, billing email, billing address (if provided), VAT/tax details (if provided)
- Invoices, payment status, and subscription details
- **Payment cards:** Payments are processed by **Stripe**. We do not store full card numbers or CVV.

**2.3 Customer Content (Uploads)**

- Images/files you upload and generation parameters you submit to produce iOS/Android icon sets
- Output files we generate for you (e.g., icon ZIPs)

> **Important notes:** Customer Content may include personal data (e.g., a photo). You are responsible for ensuring you have the necessary rights and permissions to upload and process that content. **Please do not upload Protected Health Information (PHI), regulated financial account data, or any other highly sensitive regulated data unless we have agreed in writing that we will process it.**

**2.4 Technical & Usage Data**

We collect technical and usage data to operate, secure, and improve the Services, such as:

- IP address and approximate location derived from IP (e.g., city/country)
- Device and browser information
- API key used (where applicable)
- Request/response timestamps, error traces, and rate-limit counters
- Basic performance and reliability metrics
- We may use third-party analytics tools (e.g., **Google Analytics**) to help understand website usage (see §9).

**2.5 Support & Communications**

- Messages you send to us (e.g., email content, attachments)
- Metadata associated with support requests (e.g., timestamps)

## 3) How We Use Data (Purposes & Legal Bases)

| Purpose                         | Examples                                                              | Legal basis (GDPR, where applicable)                          |
| ------------------------------- | --------------------------------------------------------------------- | ------------------------------------------------------------- |
| **Provide the Services**        | Account creation, authentication, generating and delivering icon ZIPs | Contract necessity (Art. 6(1)(b))                             |
| **Security & Abuse Prevention** | Rate limiting, fraud detection, investigating misuse                  | Legitimate interests (Art. 6(1)(f))                           |
| **Billing & Tax**               | Subscription charging, invoicing, accounting                          | Contract necessity; legal obligation                          |
| **Reliability & Analytics**     | Debugging, performance tuning, understanding product usage            | Legitimate interests and/or consent for non-essential cookies |
| **Support**                     | Responding to enquiries and requests                                  | Contract necessity; legitimate interests                      |
| **Legal Compliance**            | Responding to lawful requests; enforcing terms                        | Legal obligation; legitimate interests                        |

**Note on AI training:** We do **not** use your specific Customer Content or generated outputs to train generalized machine-learning models. We may use **aggregated and de-identified analytics** (that cannot reasonably identify you) to improve the Services.

## 4) Sharing, Service Providers & International Transfers

**4.1 Who we share data with**

We share personal data with trusted service providers ("subprocessors") to operate the Services:

| Vendor                          | Role                                             | Data Categories                        | Region             |
| ------------------------------- | ------------------------------------------------ | -------------------------------------- | ------------------ |
| **Amazon Web Services (AWS)**   | Hosting, storage (S3), compute (Lambda), logging | Customer Content, technical/usage data | **US (us-east-1)** |
| **Supabase**                    | Authentication & database                        | Account/auth data                      | **Singapore**      |
| **Stripe**                      | Payments & billing                               | Billing and subscription data          | **Multi-region**   |
| **Google Analytics**            | Website analytics                                | Usage and device data (see §9)         | **Global / US**    |
| **Transactional Email Provider (disclosed on request)** | Transactional email delivery (system notifications) | Account email, notices | **Varies by provider** |

We may also share data:

- With professional advisers (lawyers, accountants) where necessary.
- For legal reasons (to comply with law, respond to lawful requests, protect rights/safety, and enforce our terms).
- In a business transfer (e.g., merger, acquisition, financing, sale of assets). If this happens, we will require appropriate confidentiality and notify users where required by law.

**No third-party direct marketing:** We do not provide your personal data to third parties for their own direct marketing.

### 4.2 International data transfers

**4.2.1 Infrastructure**

EasyAppIcon is a global service. Our primary hosting and data services are located in the **United States** and **Singapore**.

**4.2.2 Transfer mechanisms & safeguards**

Your personal data may be processed and stored outside of your home jurisdiction. Where required by law, we use **appropriate transfer mechanisms** and **technical and organizational safeguards**. For example, for transfers of personal data from the European Economic Area (EEA), the UK, and Switzerland, we utilize **Standard Contractual Clauses (SCCs)** approved by the European Commission (and, where applicable, the **UK Addendum** or other equivalent UK transfer tool), along with supplementary safeguards designed to help maintain an appropriate level of protection for your personal data, regardless of where it is processed.

## 5) Your Rights & Choices

We believe you should have control over your personal data regardless of where you live. Depending on your location, your rights may include:

### 5.1 Rights (EEA/UK/Switzerland and other regions with GDPR-style rights)

If you are in the **EEA**, **UK**, **Switzerland**, or another region with similar laws, you may have rights to:

- **Access:** request a copy of the personal data we hold about you.
- **Correction (rectification):** request that inaccurate or incomplete personal data be corrected.
- **Deletion (erasure):** request deletion of your personal data, subject to legal exceptions (e.g., tax, security, or fraud-prevention records).
- **Restriction & objection:** restrict or object to certain processing, including processing based on legitimate interests.
- **Portability:** receive certain data in a structured, commonly used, machine-readable format and request transfer where technically feasible.
- **Withdraw consent:** where we rely on consent (for example, for non-essential cookies/analytics where required), you can withdraw it at any time.

### 5.2 Rights (Hong Kong)

Under the **Hong Kong Personal Data (Privacy) Ordinance (PDPO)**, you generally have rights to:

- **Access:** request a copy of the personal data we hold about you.
- **Correction:** request correction of inaccurate personal data.

For **data access** and **data correction** requests, we aim to respond within the statutory timeframe of **40 days** after receiving a valid request.

We do not use your personal data for **direct marketing** without your prior consent where required.

### 5.3 How to exercise your rights

Please email **[supports@easyappicon.com](mailto:supports@easyappicon.com)** from your account email with the subject line **"Privacy Rights Request"** and describe your request.

For your protection, we may ask you to verify your identity (for example, by confirming you control the email address on the account) before we fulfill the request.

### 5.4 US State Privacy Notice (including California)

If you are a resident of certain US states, you may have the right to:

- **Access / Know**: request information about the personal data we collect, use, and disclose.
- **Delete**: request deletion of personal data (subject to legal exceptions).
- **Correct**: request correction of inaccurate personal data.
- **Data Portability**: obtain a portable copy of certain data.
- **Opt-out**: opt out of **targeted advertising**, and in some states, opt out of certain **profiling** in furtherance of decisions that produce legal or similarly significant effects.
- **Limit Sensitive Data (where applicable)**: request to limit the use and disclosure of **sensitive personal information** if required by law.

**We do not sell personal information.** We also do not share personal information for cross-context behavioral advertising (as those terms are defined under California law). If this changes, we will provide the required opt-out mechanisms.

**Authorized agents (California):** California residents may use an authorized agent to submit requests on their behalf, subject to verification requirements.

**Appeals (some states):** If we deny a request, you may have the right to appeal our decision. You may do so by contacting us at **supports@easyappicon.com** with the subject line **"Privacy Appeal"**.

**Opt-out preference signals (GPC):** Where required by applicable law, we honor opt-out preference signals (such as **Global Privacy Control**) as a valid request to opt out of targeted advertising and any activity treated as "sale"/"sharing" under relevant state laws.

### 5.5 Automated decision-making

We do not make automated decisions about you that produce legal or similarly significant effects (as described under applicable data protection laws).

### 5.6 Complaints

If you have concerns about our privacy practices, please contact us first at **supports@easyappicon.com**. You also have the right to lodge a complaint with your local data protection authority. For example, in the UK this is the **Information Commissioner's Office (ICO)**; in Hong Kong this is the **Office of the Privacy Commissioner for Personal Data (PCPD)**; and in the EEA you may contact your relevant national supervisory authority.

## 6) Security

We use technical and organizational measures designed to protect personal data, including: TLS 1.2+ in transit; encryption at rest where applicable (e.g., S3 server-side encryption); least-privilege access controls; MFA for admin accounts; logging/monitoring; and routine backups.

No system is 100% secure, but we work to protect data using industry-standard practices.

**Data breach notification:** If we become aware of a personal data breach, we will assess it promptly and notify affected users and/or relevant regulators **when required by law**. Where the GDPR applies and notification is required, we generally aim to notify the competent supervisory authority **within 72 hours** of becoming aware of the breach, where feasible.

## 7) Retention & Deletion

We retain personal data only as long as necessary to provide the Services and fulfill the purposes described in §3, unless a longer retention period is required or permitted by law.

| Data Category                                | Retention Period                                                           | Reason for Retention                                 | Deletion Method                                          |
| -------------------------------------------- | -------------------------------------------------------------------------- | ---------------------------------------------------- | -------------------------------------------------------- |
| **Account data**                             | Life of account + up to 24 months                                          | Account administration, security, dispute resolution | Scheduled purge and/or anonymization                     |
| **Billing records**                          | 7 years (tax/audit, where applicable)                                      | Accounting, tax, and audit obligations               | Retained via Stripe and accounting requirements          |
| **Uploaded images/files (Customer Content)** | Typically deleted shortly after output delivery (generally within minutes) | Generate and deliver requested outputs               | Automated deletion via lifecycle rules and deletion jobs |
| **API logs/metrics**                         | ~90 days                                                                   | Security monitoring, abuse prevention, debugging     | Log retention policy                                     |
| **Analytics data**                           | Per analytics settings (e.g., 14 months)                                   | Product measurement and improvement                  | Auto-expiry configured in analytics                      |
| **Support communications**                   | Up to 24 months                                                            | Customer support continuity and issue tracking       | Manual or scheduled purge                                |

We may retain certain data longer if required to comply with law, resolve disputes, or enforce agreements. When data is retained for high-level service analytics, we aim to use aggregated or de-identified data where appropriate.

## 8) Children

The Services are not directed to children under **16** and we do not knowingly collect personal data from them. We rely on users to self-certify their age when using the Services. If we learn that we have collected personal data from a child under 16, we will take steps to **delete the data** and **terminate or restrict** the account as appropriate.

## 9) Cookies & Analytics

We use cookies and similar technologies to operate the site and understand usage.

**9.1 Essential cookies**

These are required for the Services to function (e.g., authentication/session tokens, security cookies). You cannot opt out of essential cookies.

**9.2 Analytics cookies (Google Analytics)**

We use Google Analytics to understand how visitors use our website (e.g., pages visited, session duration, device/browser info). Google may process identifiers and technical data (which can include IP address and device identifiers) to provide analytics and measurement services.

**Your choices:**

- You can control cookies through browser settings.
- If you are in the **EEA/UK** (and other regions where required), we will request your consent before loading non-essential analytics cookies.
- You can withdraw your consent or change your choices at any time by clicking **"Cookie Preferences"** (or "Privacy Settings") in the footer of our website.
- For more information on how Google processes data, see **Google's Privacy Policy**: https://policies.google.com/privacy.
- You can opt out of Google Analytics by using the **Google Analytics Opt-out Browser Add-on**: https://tools.google.com/dlpage/gaoptout

**9.3 Third-party links**

Our Services may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.

## 10) Data Processing Addendum (DPA)

If you use our API to process personal data within Customer Content and we act as a Processor, our **[DPA](/dpa)** applies. The DPA describes processor obligations, subprocessors, international transfer mechanisms (including SCCs where applicable), security measures, and deletion timelines, and is incorporated into our Terms of Service.

## 11) Changes to This Privacy Policy

We may update this Privacy Policy to reflect changes to our practices or for legal, technical, or regulatory reasons. When we make material changes, we will provide reasonable notice (e.g., in-product message or email). The "Effective date" above indicates when this version took effect.

## 12) Contact

Questions or requests about privacy?
**Email:** **[supports@easyappicon.com](mailto:supports@easyappicon.com)** (Attn: **Privacy Officer**)

**Data Protection Officer (DPO):** We will appoint a DPO if required by applicable law and will update this Policy accordingly.

**Mail:** *West Wing 2/F, 822 Lai Chi Kok Road, Cheung Sha Wan, Kowloon, Hong Kong*
`

const PrivacyPolicy = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="January 21, 2026"
      metaDescription="EasyAppIcon Privacy Policy - Learn how we collect, use, disclose, and protect your personal data when using our app icon generator service."
      content={content}
    />
  )
}

export default PrivacyPolicy
